<script lang="ts">
export const elButtonSizes = ['xs', 'sm', 'base', 'l', 'xl'] as const;
export type ElButtonSize = (typeof elButtonSizes)[number];

export const elButtonVariants = ['primary', 'secondary', 'tertiary'] as const;
export type ElButtonVariant = (typeof elButtonVariants)[number];
</script>

<script setup lang="ts">
import ElIcon, { ElIconProps } from '@/ElIcon.vue';
import { ref, computed, ButtonHTMLAttributes } from 'vue';

export interface ElButtonProps {
  disabled?: boolean;
  dark?: boolean;
  loading?: boolean;
  icon?: ElIconProps;
  iconRight?: boolean;
  error?: boolean;
  label: string;
  size?: ElButtonSize;
  variant?: ElButtonVariant;
  type?: ButtonHTMLAttributes['type'];
  loadOnClick?: boolean;
  onClick?: (() => unknown) | (() => Promise<unknown>);
}

const props = withDefaults(defineProps<ElButtonProps>(), {
  disabled: false,
  dark: false,
  loading: false,
  error: false,
  label: '',
  icon: undefined,
  size: 'base',
  variant: 'primary',
  type: 'button',
  loadOnClick: false,
  onClick: undefined,
});

const callbackLoading = ref<boolean>(false);
const defaultLoading = ref<boolean>(false);
const computedLoading = computed(() => callbackLoading.value || defaultLoading.value || props.loading);

const onClick = async () => {
  if (callbackLoading.value || computedLoading.value) return;
  defaultLoading.value = callbackLoading.value = props.loadOnClick;
  setTimeout(() => {
    defaultLoading.value = false;
  }, 3000);
  await props.onClick?.();
  callbackLoading.value = false;
};
</script>

<template>
  <button
    :disabled="disabled || loading"
    :type="type"
    class="inline-flex items-center justify-center py-0 font-semibold leading-4 transition duration-150 ease-in-out"
    :class="{
      'h-6 rounded text-xs': size === 'xs',
      'h-7 rounded text-xs': size === 'sm',
      'h-8 rounded-md text-sm': size === 'base',
      'text-base h-10 rounded-md ': size === 'l',
      'text-base h-12 rounded-md ': size === 'xl',
      'cursor-pointer': !computedLoading && !disabled,
      'cursor-wait': computedLoading && !disabled,
      'px-4': variant !== 'tertiary' && (size === 'base' || size === 'l' || size === 'xl'),
      'px-3': (variant !== 'tertiary' && (size === 'sm' || size === 'xs')) || (variant === 'tertiary' && (size === 'l' || size === 'xl')),
      'px-2': variant === 'tertiary' && (size === 'xs' || size === 'sm' || size === 'base'),
      ...(dark
        ? {
            'bg-alpha-200 text-white hover:bg-alpha-300 focus:bg-alpha-300 focus:border focus:border-alpha-400 focus:outline-none':
              variant === 'primary' && !disabled,
            'bg-alpha-100 text-alpha-400 cursor-not-allowed': variant === 'primary' && disabled,
            'bg-black border border-alpha-500 hover:bg-alpha-100 focus:bg-alpha-100 text-white focus:outline-none':
              variant === 'secondary' && !error && !disabled,
            'text-alpha-400 bg-alpha-100 cursor-not-allowed focus:outline-none': variant === 'secondary' && disabled,
            'bg-transparent hover:bg-alpha-100 focus:bg-alpha-100 border-none text-white focus:outline-none':
              variant === 'tertiary' && !error && !disabled,
            'text-alpha-400 cursor-not-allowed border-none bg-transparent focus:outline-none': variant === 'tertiary' && disabled,
          }
        : {
            'bg-primary hover:bg-primary-hover active:bg-primary-pressed active:outlined-primary text-white':
              variant === 'primary' && !error && !disabled,
            'bg-error hover:bg-error-hover active:bg-error-pressed active:outlined-error text-white':
              variant === 'primary' && error && !disabled,
            'bg-neutral-inactive text-neutral-inactive cursor-not-allowed': variant === 'primary' && disabled,
            'bg-white border border-primary hover:border-primary-hover hover:bg-primary-light-hover hover:text-primary-hover active:text-primary-pressed active:bg-primary-light active:border-primary-pressed text-primary active:outlined-primary':
              variant === 'secondary' && !error && !disabled,
            'border-error text-error hover:bg-error-light-hover active:bg-error-light-pressed active:outlined-error border bg-white':
              variant === 'secondary' && error && !disabled,
            'text-neutral-inactive border-neutral-inactive cursor-not-allowed border': variant === 'secondary' && disabled,
            'text-primary hover:text-primary-hover hover:bg-primary-light-hover active:text-primary-pressed active:bg-primary-light-pressed active:outlined-primary border-none bg-transparent':
              variant === 'tertiary' && !error && !disabled,
            'text-error hover:bg-error-light-hover active:bg-error-light-pressed active:outlined-error border-none':
              variant === 'tertiary' && error && !disabled,
            'text-neutral-inactive cursor-not-allowed border-none bg-transparent': variant === 'tertiary' && disabled,
          }),
    }"
    :data-cy="`${label.trim()?.replaceAll(' ', '').replaceAll(',', '')}-button`"
    @click="onClick"
  >
    <span v-if="!computedLoading" class="inline-flex items-center" :class="iconRight ? 'flex-row-reverse' : 'flex-row'">
      <ElIcon
        v-if="icon"
        :name="icon.name"
        :solid="icon.solid"
        :class="{
          'h-[14px] w-[14px]': size === 'xs',
          'h-4 w-4': size === 'sm',
          'h-5 w-5': size === 'base',
          'h-6 w-6': size === 'l' || size === 'xl',
        }"
        :color="icon.color"
      />
      <span
        v-if="icon && label !== ''"
        :class="{
          'w-1': size === 'xs',
          'w-2': size === 'sm' || size === 'base',
          'w-3': size === 'l' || size === 'xl',
        }"
      ></span>
      <span class="font-semibold">{{ label }}</span>
    </span>
    <span v-else class="inline-flex items-center">
      <svg
        class="animate-spin"
        :class="{
          'mr-1 h-[14px] w-[14px]': size === 'xs',
          'mr-2 h-4 w-4': size === 'sm',
          'mr-2 h-5 w-5': size === 'base',
          'mr-3 h-6 w-6': size === 'l' || size === 'xl',
        }"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span class="font-semibold">Un attimo...</span>
    </span>
  </button>
</template>
